import type { MeResponse } from '@platform/contracts';
import {
  Monogram,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  cn,
} from '@platform/ui';
import { Building2 } from 'lucide-react';

import { buildScopeOptions } from '../lib/scope';

export interface ScopeSelectorProps {
  me: Pick<MeResponse, 'clients'>;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

/**
 * Selector de empresa, siempre a la vista: "Empresa: Todas las empresas / Sweet Papaya…". Todo el
 * panel muestra los datos de lo que esté elegido acá. Agrupado por cliente cuando hay varios.
 */
export function ScopeSelector({ me, value, onChange, className }: ScopeSelectorProps) {
  const options = buildScopeOptions(me);
  const groups = [...new Set(options.flatMap((o) => (o.group ? [o.group] : [])))];
  const single = options.length === 2 && options[1]?.kind === 'tenant';
  const pickedTenant = options.some((o) => o.value === value && o.kind === 'tenant');
  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <span aria-hidden className="hidden text-sm text-muted-foreground sm:inline">
        Empresa
      </span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className="h-10 w-auto min-w-0 max-w-[15rem] gap-2 rounded-full border-border bg-card pr-3 pl-2.5 font-medium sm:max-w-xs"
          aria-label="Empresa"
        >
          {pickedTenant ? null : (
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent text-highlight">
              <Building2 className="size-3.5" aria-hidden />
            </span>
          )}
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="min-w-60">
          <SelectItem value="all">Todas las empresas</SelectItem>
          {groups.map((g) => (
            <SelectGroup key={g}>
              <SelectSeparator />
              {single ? null : <SelectLabel>{g}</SelectLabel>}
              {options
                .filter((o) => o.group === g)
                .map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.kind === 'tenant' ? (
                      <span className="flex min-w-0 items-center gap-2">
                        <Monogram
                          name={o.label}
                          colorKey={o.slug ?? o.label}
                          src={o.logoUrl}
                          size="xs"
                          className="size-6 rounded-full"
                        />
                        <span className="truncate">{o.label}</span>
                      </span>
                    ) : (
                      o.label
                    )}
                  </SelectItem>
                ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
